import React, { useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { IoIosArrowBack } from "react-icons/io";
import { FaUser } from "react-icons/fa";
import { IoMdSettings } from "react-icons/io";
import { useQuery } from "@tanstack/react-query";
import clsx from "clsx";
import UserComments from "./components/UserComments";
import UserProperties from "./components/UserProperties";
import UserSettings from "./components/UserSettings";
import userProfilePicture from "../../../../../assets/icons-v2/user_profile_picture.png";
import userPoints from "../../../../../assets/icons-v2/user_points.png";
import { findUser, getUserSocialPoints } from "../../../../../services/UserService";
import { useUser } from "../../../../../context/UserContext";
import Skeleton from "../../../../../components/common/Skeleton";

const UserProfileSkeleton = () => (
    <div className="tw-max-w-[600px] tw-m-auto tw-flex tw-flex-col tw-min-h-screen">
        <div className="tw-bg-white tw-flex tw-items-center tw-justify-between tw-h-[60px] tw-border-b tw-border-b-[#D9D9D9]">
            <div className="tw-w-6"></div>
            <Skeleton className="tw-w-32 tw-h-6 tw-rounded" />
            <div className="tw-w-6" />
        </div>

        <div className="tw-bg-white tw-p-6 tw-m-4 tw-rounded-2xl tw-shadow-[0_4px_8px_rgba(0,0,0,0.1)]">
            <div className="tw-flex tw-items-center tw-gap-4">
                <Skeleton className="tw-w-12 tw-h-12 tw-rounded-full" />
                <div className="tw-flex tw-flex-col tw-gap-2">
                    <Skeleton className="tw-w-24 tw-h-4 tw-rounded" />
                    <Skeleton className="tw-w-20 tw-h-4 tw-rounded" />
                </div>
            </div>
        </div>
    </div>
);

const UserProfile = () => {
    const [activeTab, setActiveTab] = useState("comments");
    const [isEditing, setIsEditing] = useState(false);
    const navigate = useNavigate();
    const { user_id } = useParams();
    const { userState } = useUser();

    const { data: userData, isLoading: isLoadingUser } = useQuery({
        queryKey: ["user", user_id],
        queryFn: () => findUser(user_id),
        enabled: !!user_id,
    });

    const { data: userPointsData, isLoading: isLoadingPoints } = useQuery({
        queryKey: ["userPoints", user_id],
        queryFn: () => getUserSocialPoints(user_id),
        enabled: !!user_id,
    });

    const handleBackClick = () => {
        if (isEditing) {
            setIsEditing(false);
        } else {
            navigate(-1);
        }
    };

    if (isLoadingUser || isLoadingPoints) {
        return <UserProfileSkeleton />;
    }

    const user = userData ?? {};
    const isOwnProfile = userState?.id === user_id;
    const profileTitle = isOwnProfile ? "My Profile" : `${user.nickname}'s Profile`;
    const points = userPointsData?.data?.points ?? 0;

    return (
        <div className="tw-max-w-[600px] tw-m-auto tw-flex tw-flex-col tw-min-h-screen">
            {/* Navigation Bar */}
            <div className="tw-bg-white tw-flex tw-items-center tw-justify-between tw-h-[60px] tw-border-b tw-border-b-[#D9D9D9]">
                <button
                    onClick={handleBackClick}
                    className="tw-fixed tw-top-[10svh] tw-left-4 md:tw-left-16 lg:tw-left-[25rem] tw-z-[50] tw-rounded-full tw-bg-black/50"
                >
                    <IoIosArrowBack className="tw-h-6 tw-w-6 md:tw-h-7 md:tw-w-7 lg:tw-h-8 lg:tw-w-8 tw-text-white tw-cursor-pointer" />
                </button>
                <h1 className="tw-text-center tw-font-bold tw-text-[18px] tw-font-merriweather tw-p-0 tw-m-0 tw-absolute tw-left-1/2 tw-transform -tw-translate-x-1/2">
                    {isEditing ? "Edit Profile Details" : profileTitle}
                </h1>
                {/* keep this empty div for spacing so the title goes to the center due to tw-flex tw-items-center tw-justify-between  */}
                <div className="tw-w-6"></div>
            </div>

            {isEditing ? (
                <UserSettings user={user} />
            ) : (
                <>
                    {/* User Profile Card */}
                    <div className="tw-bg-white tw-p-6 tw-m-4 tw-rounded-2xl tw-shadow-[0_4px_8px_rgba(0,0,0,0.1)]">
                        <div className="tw-flex tw-items-center tw-justify-between">
                            <div className="tw-flex tw-items-center tw-gap-4">
                                <div className="tw-w-12 tw-h-12 tw-bg-white tw-flex tw-items-center tw-justify-center">
                                    <img
                                        src={userProfilePicture}
                                        alt="Profile"
                                        className="tw-w-[45px] tw-h-[45px]`"
                                    />
                                </div>
                                <div className="tw-flex tw-flex-col tw-font-merriweather tw-gap-1">
                                    <div className="tw-flex tw-items-center tw-gap-2">
                                        <FaUser className="tw-text-[#FF8A65]" size={15} />
                                        <span className="tw-text-[14px] tw-font-bold">{user.nickname}</span>
                                    </div>
                                    <div className="tw-flex tw-items-center tw-gap-2 tw-mt-1">
                                        <img
                                            src={userPoints}
                                            alt="Points"
                                            className="tw-w-[15px] tw-h-[15px]"
                                        />
                                        <span className="tw-text-[14px] tw-font-bold">{points} Points</span>
                                    </div>
                                </div>
                            </div>
                            {isOwnProfile && (
                                <button
                                    onClick={() => setIsEditing(true)}
                                    className="tw-bg-white tw-p-2 tw-rounded-full hover:tw-bg-gray-100 tw-transition-colors"
                                >
                                    <IoMdSettings size={24} className="tw-text-[#666666]" />
                                </button>
                            )}
                        </div>
                    </div>

                    {/* Tabs */}
                    <div className="tw-flex tw-bg-white tw-mx-6 tw-text-[16px] tw-mb-2">
                        {/* <button
                            className={clsx(
                                "tw-bg-white tw-flex-1 tw-py-4 tw-text-center tw-relative",
                                activeTab === "properties"
                                    ? "tw-text-[#6C26C4] tw-font-medium"
                                    : "tw-text-[#100F13]"
                            )}
                            onClick={() => setActiveTab("properties")}
                            style={{ borderBottom: "1px solid #D9D9D9", borderRight: "1px solid #D9D9D9" }}
                        >
                            {isOwnProfile && "My"} Properties
                            {activeTab === "properties" && (
                                <div className="tw-absolute tw-bottom-0 tw-left-1/2 tw-transform -tw-translate-x-1/2 tw-w-[80%] tw-h-[2px] tw-bg-[#6C26C4]" />
                            )}
                        </button> */}
                        <div className="tw-w-[2px] tw-bg-[#D9D9D9] tw-h-full"></div>
                        <button
                            className={clsx(
                                "tw-bg-white tw-flex-1 tw-py-4 tw-text-center tw-relative",
                                activeTab === "comments"
                                    ? "tw-text-[#6C26C4] tw-font-medium"
                                    : "tw-text-[#100F13]"
                            )}
                            onClick={() => setActiveTab("comments")}
                            style={{ borderBottom: "1px solid #D9D9D9" }}
                        >
                            {isOwnProfile && "My"} Comments
                            {activeTab === "comments" && (
                                <div className="tw-absolute tw-bottom-0 tw-left-1/2 tw-transform -tw-translate-x-1/2 tw-w-[80%] tw-h-[2px] tw-bg-[#6C26C4]" />
                            )}
                        </button>
                    </div>

                    {/* Content */}
                    <div className="tw-flex-1 tw-overflow-y-auto">
                        {activeTab === "properties" ? (
                            <UserProperties userId={user_id} />
                        ) : (
                            <UserComments userId={user_id} isOwnProfile={isOwnProfile} />
                        )}
                    </div>
                </>
            )}
        </div>
    );
};

export default UserProfile;
